import { useInView } from "../../hooks/useInView";

interface Benefit {
  icon: string;
  title: string;
  body: string;
}

interface WhyChooseUsProps {
  heading?: string;
  benefits?: Benefit[];
  quote?: string;
  quoteAuthor?: string;
}

const defaultBenefits: Benefit[] = [
  {
    icon: "ri-medal-fill",
    title: "30 Years of Landscape Craftsmanship",
    body: "Three decades of designing and building outdoor spaces in London and Southwestern Ontario. Our track record of landscape transformations across the region speaks for itself.",
  },
  {
    icon: "ri-user-heart-fill",
    title: "Artisan Craftsmanship, Every Project",
    body: "Every detail handled by our own in-house crews \u2014 never subcontracted. From the first stone placed to the final walkthrough, our standard of quality never wavers.",
  },
  {
    icon: "ri-leaf-fill",
    title: "Environmentally Conscious Design",
    body: "We design with the land, not against it. Our projects incorporate sustainable materials, proper drainage, and eco-conscious plant selection that benefits your property long-term.",
  },
  {
    icon: "ri-customer-service-2-fill",
    title: "Clear Communication, No Surprises",
    body: "Transparent pricing, scheduled timelines, and a dedicated point of contact from consultation to final walkthrough. You always know exactly what to expect.",
  },
];

export default function WhyChooseUs({
  heading = "Why London\u2019s Most Discerning Homeowners Choose Us",
  benefits = defaultBenefits,
  quote = "We don\u2019t just build landscapes. We build the outdoor environments families will gather in for generations.",
  quoteAuthor = "Upper Canada Landscape \u2014 Serving London Since 1994",
}: WhyChooseUsProps) {
  const { ref, inView } = useInView(0.08);

  return (
    <section ref={ref} className="bg-charcoal py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">

          {/* LEFT: heading + benefits */}
          <div>
            <span className={`font-sans text-xs font-semibold tracking-[0.22em] uppercase text-rust-400 mb-4 block reveal ${inView ? "visible" : ""}`}>
              Why Upper Canada Landscape
            </span>
            <h2 className={`font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-12 reveal ${inView ? "visible" : ""} reveal-delay-1`}>
              {heading}
            </h2>

            <div className="space-y-7">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className={`flex gap-4 group reveal ${inView ? "visible" : ""} reveal-delay-${i + 2}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-rust-700/20 border border-rust-700/25 flex-shrink-0 mt-0.5 group-hover:bg-rust-700/35 transition-colors">
                    <i className={`${b.icon} text-rust-400 text-sm`}></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-sm font-bold text-white mb-1.5 leading-snug">{b.title}</h3>
                    <p className="font-sans text-sm text-stone-400 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: project image + quote + stats */}
          <div className={`flex flex-col gap-5 reveal-right ${inView ? "visible" : ""}`}>

            {/* Project image */}
            <div className="rounded-xl overflow-hidden h-56 md:h-64 w-full">
              <img
                src="https://uppercanadalandscape.com/wp-content/uploads/2026/03/Stone-Waterfall-Feature-and-Backyard-Patio-Installation.webp"
                alt="Premium backyard landscape with stone patio and waterfall &ndash; Upper Canada Landscape"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
              />
            </div>

            {/* Quote card */}
            <div className="bg-charcoal-800 border border-stone-700/60 rounded-xl p-7 md:p-9 relative overflow-hidden">
              {/* Large quote mark */}
              <div className="font-serif text-8xl text-rust-800 leading-none absolute -top-3 left-5 select-none opacity-60">
                &ldquo;
              </div>
              <p className="font-serif text-lg md:text-xl text-stone-200 italic leading-relaxed mb-5 pt-4 relative z-10">
                {quote}
              </p>
              <p className="font-sans text-xs text-stone-500 relative z-10">{quoteAuthor}</p>
            </div>

            {/* Stats row — only verifiable claims */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { value: "30+", label: "Years" },
                { value: "4", label: "Core Services" },
                { value: "6+", label: "Regions" },
                { value: "100%", label: "In-House" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="bg-charcoal-800 border border-stone-700/60 rounded-xl py-4 px-3 text-center"
                >
                  <p className="font-serif text-xl font-bold text-white mb-0.5">{stat.value}</p>
                  <p className="font-sans text-[10px] text-stone-500 uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
